import { isOverdueItem } from '../../../../shared/utils/overdue';
import { getExpenseAccountingDate } from '../../utils/accountingDates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listExpensesResult,
  registerExpensePayment as registerExpensePaymentMutation,
  updateExpense as updateExpenseMutation,
  voidExpense as voidExpenseMutation,
} from '@/modules/accounting/services';
import { listStaffResult } from '@/modules/staff/services/staff.service';
import { listSuppliersResult } from '@/modules/suppliers/services/suppliers.service';
import { useAccountingFilters } from '@/modules/accounting/hooks/shared/useAccountingFilters';
import {
  isWithinNextDays,
  sortByDueDateAscending,
} from '@/modules/accounting/utils';
import { toast } from '../../../../shared/hooks/use-toast';

const INITIAL_FILTERS = {
  query: '',
  selectedType: 'tutti',
  selectedPeriod: 'tutti',
  selectedStatus: 'tutti',
  selectedRecipient: 'tutti',
};

function isActiveExpense(expense) {
  return expense?.status !== 'annullata';
}

function normalizeSearch(value) {
  return String(value || '').trim().toLowerCase();
}

function matchesSearchQuery(expense, query, recipientLabel = '') {
  const normalizedQuery = normalizeSearch(query);
  if (!normalizedQuery) return true;

  return [
    expense.description,
    expense.type,
    expense.status,
    expense.amount,
    expense.dueDate,
    expense.paymentDate,
    expense.recurring,
    expense.supplierName,
    expense.notes,
    recipientLabel,
  ].some((value) => normalizeSearch(value).includes(normalizedQuery));
}

export function useExpensesData({ periodFrom = '', periodTo = '' } = {}) {
  const [expenses, setExpenses] = useState([]);
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(true);
  const [expensesModuleDisabled, setExpensesModuleDisabled] = useState(false);
  const [expensesUnavailable, setExpensesUnavailable] = useState(false);
  const [expensesError, setExpensesError] = useState('');
  const [expensesAuthRequired, setExpensesAuthRequired] = useState(false);
  const [expensesTenantDatabaseError, setExpensesTenantDatabaseError] = useState('');
  const [expensesCatalogStatus, setExpensesCatalogStatus] = useState('idle');
  const [staff, setStaff] = useState([]);
  const [staffUnavailable, setStaffUnavailable] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierUnavailable, setSupplierUnavailable] = useState(false);
  const [supplierError, setSupplierError] = useState('');
  const { filters, setFilter } = useAccountingFilters(INITIAL_FILTERS);
  const [activeOverviewFilter, setActiveOverviewFilter] = useState(null);

  const loadExpenses = useCallback(async (isMounted = () => true) => {
      const [expensesResult, staffResult, suppliersResult] = await Promise.all([
        listExpensesResult(),
        listStaffResult(),
        listSuppliersResult(),
      ]);

      if (!isMounted()) {
        return;
      }

      const loadedExpenses = expensesResult.ok ? expensesResult.data.filter(isActiveExpense) : [];
      setExpenses(sortByDueDateAscending(loadedExpenses));
      setStaff(staffResult.ok ? staffResult.data : []);
      setStaffUnavailable(!staffResult.ok);
      setStaffError(staffResult.ok ? '' : staffResult.message || staffResult.error || '');
      setSuppliers(suppliersResult.ok ? suppliersResult.data : []);
      setSupplierUnavailable(!suppliersResult.ok);
      setSupplierError(suppliersResult.ok ? '' : suppliersResult.message || suppliersResult.error || '');
      setIsLoadingExpenses(false);
      const moduleDisabled = expensesResult.error === 'ACCOUNTING_MODULE_DISABLED';
      const tenantDatabaseError = [
        'ACCOUNTING_TENANT_DATABASE_NOT_READY',
        'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
        'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
      ].includes(expensesResult.error)
        ? expensesResult.error
        : '';
      setExpensesModuleDisabled(moduleDisabled);
      setExpensesTenantDatabaseError(tenantDatabaseError);
      setExpensesUnavailable(
        !expensesResult.ok &&
          expensesResult.error !== 'ACCOUNTING_AUTH_REQUIRED' &&
          !moduleDisabled &&
          !tenantDatabaseError
      );
      setExpensesAuthRequired(expensesResult.error === 'ACCOUNTING_AUTH_REQUIRED');
      setExpensesError(expensesResult.ok ? '' : expensesResult.message || expensesResult.error || '');
      setExpensesCatalogStatus(
        expensesResult.ok
          ? loadedExpenses.length > 0
            ? 'available'
            : 'empty'
          : moduleDisabled
            ? 'module_disabled'
          : tenantDatabaseError
            ? 'tenant_database_error'
          : expensesResult.error === 'ACCOUNTING_AUTH_REQUIRED'
            ? 'auth_required'
          : 'unavailable'
      );
  }, []);

  useEffect(() => {
    let isMounted = true;
    loadExpenses(() => isMounted);

    return () => {
      isMounted = false;
    };
  }, [loadExpenses]);

  const filteredExpensesBeforeOverview = useMemo(() => {
    const staffById = new Map(staff.map((member) => [String(member.id), member]));
    const suppliersById = new Map(suppliers.map((supplier) => [String(supplier.id), supplier]));

    function getRecipientLabel(expense) {
      if (expense.recipientType === 'STAFF') {
        const member = expense.staffId == null ? null : staffById.get(String(expense.staffId));
        return member?.name || '';
      }

      if (expense.recipientType === 'SUPPLIER') {
        const supplier = expense.supplierId == null ? null : suppliersById.get(String(expense.supplierId));
        return expense.supplierName || supplier?.displayName || supplier?.legalName || '';
      }

      return '';
    }

    const filtered = expenses.filter((expense) => {
      const matchesQuery = matchesSearchQuery(expense, filters.query, getRecipientLabel(expense));
      const matchesType =
        filters.selectedType === 'tutti' || expense.type === filters.selectedType;
      const matchesStatus =
        filters.selectedStatus === 'tutti' || expense.status === filters.selectedStatus;
      const recipientType = expense.recipientType || 'OTHER';
      const matchesRecipient =
        filters.selectedRecipient === 'tutti' ||
        (filters.selectedRecipient === 'staff' && recipientType === 'STAFF') ||
        (filters.selectedRecipient === 'fornitori' && recipientType === 'SUPPLIER') ||
        (filters.selectedRecipient === 'altro' && recipientType === 'OTHER');

      if (filters.selectedPeriod === 'scaduti') {
        return matchesQuery && matchesType && matchesRecipient && expense.status === 'in_ritardo';
      }

      if (filters.selectedPeriod === 'prossimi_30') {
        return matchesQuery && matchesType && matchesStatus && matchesRecipient && isWithinNextDays(expense.dueDate, 30);
      }

      if (filters.selectedPeriod === 'dashboard_period') {
        const accountingDate = String(getExpenseAccountingDate(expense));
        const matchesDashboardPeriod = accountingDate &&
          (!periodFrom || accountingDate >= periodFrom) &&
          (!periodTo || accountingDate <= periodTo);
        return matchesQuery && matchesType && matchesStatus && matchesRecipient && matchesDashboardPeriod;
      }

      return matchesQuery && matchesType && matchesStatus && matchesRecipient;
    });

    return sortByDueDateAscending(filtered);
  }, [expenses, filters, staff, suppliers, periodFrom, periodTo]);

  const filteredExpenses = useMemo(() => {
    if (!activeOverviewFilter || activeOverviewFilter === 'expected') return filteredExpensesBeforeOverview;
    if (activeOverviewFilter === 'paid') {
      return filteredExpensesBeforeOverview.filter((expense) => expense.status === 'pagato');
    }
    if (activeOverviewFilter === 'toPay') {
      return filteredExpensesBeforeOverview.filter((expense) =>
        ['da_saldare', 'previsto', 'in_ritardo'].includes(expense.status)
      );
    }
    if (activeOverviewFilter === 'overdue') {
      return filteredExpensesBeforeOverview.filter((expense) =>
        isOverdueItem(expense)
      );
    }
    return filteredExpensesBeforeOverview;
  }, [activeOverviewFilter, filteredExpensesBeforeOverview]);

  const stats = useMemo(() => {
    const activeExpenses = expenses.filter(isActiveExpense);
    const totalExpected = activeExpenses.reduce((sum, item) => sum + item.amount, 0);
    const totalPaid = activeExpenses
      .filter((item) => item.status === 'pagato')
      .reduce((sum, item) => sum + item.amount, 0);
    const totalRecurring = activeExpenses
      .filter((item) => item.recurring !== 'una_tantum')
      .reduce((sum, item) => sum + item.amount, 0);
    const totalPending = activeExpenses
      .filter((item) => ['da_saldare', 'previsto', 'in_ritardo'].includes(item.status))
      .reduce((sum, item) => sum + item.amount, 0);
    const totalOverdue = activeExpenses
      .filter((item) => isOverdueItem(item))
      .reduce((sum, item) => sum + item.amount, 0);

    return {
      totalExpected,
      totalPaid,
      totalRecurring,
      totalPending,
      totalOverdue,
      recurringCount: activeExpenses.filter((item) => item.recurring !== 'una_tantum').length,
      pendingCount: activeExpenses.filter((item) => ['da_saldare', 'previsto', 'in_ritardo'].includes(item.status)).length,
      totalCount: activeExpenses.length,
      percentagePaid:
        totalExpected > 0 ? ((totalPaid / totalExpected) * 100).toFixed(1) : '0.0',
    };
  }, [expenses]);

  const upcomingExpenses = useMemo(() => {
    return sortByDueDateAscending(
      filteredExpenses.filter(
        (expense) =>
          (expense.status === 'da_saldare' || expense.status === 'previsto') &&
          isWithinNextDays(expense.dueDate, 7)
      )
    );
  }, [filteredExpenses]);

  async function createExpenseResult() {
    await loadExpenses();
  }

  async function registerExpensePayment(expenseId) {
    const result = await registerExpensePaymentMutation(expenseId);
    if (!result.ok) {
      toast({ title: 'Pagamento non registrato', description: result.message, variant: 'destructive' });
      return result;
    }

    setExpenses((prev) =>
      prev.map((item) => (item.id === result.data.expense.id ? result.data.expense : item))
    );
    toast({ title: 'Pagamento registrato', description: result.message });
    return result;
  }

  async function updateExpense(expenseId, form) {
    const result = await updateExpenseMutation(expenseId, form);
    if (!result.ok) {
      toast({ title: 'Uscita non aggiornata', description: result.message, variant: 'destructive' });
      return result;
    }

    await loadExpenses();
    toast({ title: 'Uscita aggiornata', description: result.message });
    return result;
  }

  async function voidExpense(expenseId) {
    const result = await voidExpenseMutation(expenseId);
    if (!result.ok) {
      toast({ title: 'Uscita non annullata', description: result.message, variant: 'destructive' });
      return result;
    }

    setExpenses((prev) => prev.filter((item) => item.id !== result.data.expense.id));
    toast({ title: 'Uscita annullata', description: result.message });
    return result;
  }

  function setOverviewFilter(filterKey) {
    setActiveOverviewFilter((prev) => {
      const next = prev === filterKey ? null : filterKey;
      Object.entries(INITIAL_FILTERS).forEach(([key, value]) => setFilter(key, value));
      return next;
    });
  }

  function clearOverviewFilter() {
    setActiveOverviewFilter(null);
  }

  return {
    expenses,
    isLoadingExpenses,
    expensesModuleDisabled,
    expensesUnavailable,
    expensesError,
    expensesAuthRequired,
    expensesTenantDatabaseError,
    expensesCatalogStatus,
    staff,
    staffUnavailable,
    staffError,
    suppliers,
    supplierUnavailable,
    supplierError,
    filteredExpenses,
    upcomingExpenses,
    stats,
    filters,
    setFilter,
    activeOverviewFilter,
    setOverviewFilter,
    clearOverviewFilter,
    createExpenseResult,
    updateExpense,
    registerExpensePayment,
    voidExpense,
  };
}
